type InspectorTabKey = "design" | "data" | "prototype";

interface InspectorTabState {
    scrollByTab: Map<string, number>;
    disposers: Array<() => void>;
    activeIndex: number;
}

const TAB_KEYS: InspectorTabKey[] = ["design", "data", "prototype"];

export class InspectorTabsAdapter {
    static #activeTab: InspectorTabKey = "design";
    static readonly #instances = new WeakMap<HTMLElement, InspectorTabState>();
    static readonly #roots = new Set<HTMLElement>();

    static init(scope: ParentNode): void {
        this.destroyDisconnected();
        scope.querySelectorAll<HTMLElement>("[data-cms-inspector-tabs]").forEach((root) => {
            if (this.#instances.has(root)) return;
            this.#initOne(root);
        });
    }

    static destroy(scope?: ParentNode): void {
        const roots = scope
            ? [...scope.querySelectorAll<HTMLElement>("[data-cms-inspector-tabs]")]
            : [...this.#roots];
        roots.forEach((root) => this.#destroyOne(root));
    }

    static destroyDisconnected(): void {
        [...this.#roots].forEach((root) => {
            if (!document.documentElement.contains(root)) {
                this.#destroyOne(root);
            }
        });
    }

    static instanceCount(): number {
        this.destroyDisconnected();
        return this.#roots.size;
    }

    static currentTab(): InspectorTabKey {
        return this.#activeTab;
    }

    static #initOne(root: HTMLElement): void {
        const tabs = [...root.querySelectorAll<HTMLButtonElement>("[data-cms-inspector-tab]")];
        const panels = [...root.querySelectorAll<HTMLElement>("[data-cms-inspector-panel]")];
        const scrollPanels = [...root.querySelectorAll<HTMLElement>("[data-cms-inspector-panel-scroll]")];
        const viewport = root.querySelector<HTMLElement>("[data-cms-inspector-tab-panels]");
        if (tabs.length === 0 || panels.length === 0 || !viewport) return;

        const supportedKeys = panels
            .map((panel) => panel.dataset.cmsInspectorPanel ?? "")
            .filter((key): key is InspectorTabKey => TAB_KEYS.includes(key as InspectorTabKey));
        const initial = supportedKeys.includes(this.#activeTab) ? this.#activeTab : supportedKeys[0] ?? "design";
        const scrollByTab = new Map<string, number>();
        const disposers: Array<() => void> = [];
        scrollPanels.forEach((panel) => {
            const onScroll = (): void => {
                const key = panel.dataset.cmsInspectorPanelScroll ?? "";
                if (key !== "") scrollByTab.set(key, panel.scrollTop);
            };
            panel.addEventListener("scroll", onScroll, { passive: true });
            disposers.push(() => panel.removeEventListener("scroll", onScroll));
        });

        const state: InspectorTabState = {
            scrollByTab,
            disposers,
            activeIndex: Math.max(0, supportedKeys.indexOf(initial)),
        };
        this.#instances.set(root, state);
        this.#roots.add(root);
        root.dataset.inspectorTabsMounted = "1";

        const activate = (key: InspectorTabKey, focusTab = false): void => {
            const index = supportedKeys.indexOf(key);
            if (index < 0) return;
            this.#activeTab = key;
            state.activeIndex = index;
            this.#sync(root, tabs, panels, key);
            window.requestAnimationFrame(() => {
                const panel = scrollPanels.find((candidate) => candidate.dataset.cmsInspectorPanelScroll === key);
                if (panel) panel.scrollTop = scrollByTab.get(key) ?? 0;
                this.#writeMetrics(root, state);
                if (focusTab) tabs[index]?.focus();
            });
        };

        activate(initial);

        const onKeydown = (event: KeyboardEvent): void => {
            if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
            const target = event.target as HTMLElement | null;
            const focusedTab = document.activeElement?.closest<HTMLElement>("[data-cms-inspector-tab]");
            const eventTab = target?.closest<HTMLElement>("[data-cms-inspector-tab]") ?? focusedTab;
            if (!eventTab || !root.contains(eventTab)) return;
            event.preventDefault();
            const activeIndex = Math.max(0, supportedKeys.indexOf(this.#activeTab));
            const delta = event.key === "ArrowRight" ? 1 : -1;
            const next = (activeIndex + delta + supportedKeys.length) % supportedKeys.length;
            const key = supportedKeys[next] ?? "design";
            activate(key, true);
        };
        window.addEventListener("keydown", onKeydown, true);
        root.addEventListener("keydown", onKeydown, true);
        document.addEventListener("keydown", onKeydown, true);
        disposers.push(() => window.removeEventListener("keydown", onKeydown, true));
        disposers.push(() => root.removeEventListener("keydown", onKeydown, true));
        disposers.push(() => document.removeEventListener("keydown", onKeydown, true));

        tabs.forEach((tab) => {
            const onClick = (): void => {
                const key = this.#normalizeTab(tab.dataset.cmsInspectorTab);
                activate(key);
            };
            tab.addEventListener("click", onClick);
            disposers.push(() => tab.removeEventListener("click", onClick));
        });

        window.requestAnimationFrame(() => {
            this.#writeMetrics(root, state);
        });
    }

    static #sync(
        root: HTMLElement,
        tabs: HTMLButtonElement[],
        panels: HTMLElement[],
        activeKey: InspectorTabKey,
    ): void {
        root.dataset.activeInspectorTab = activeKey;
        tabs.forEach((tab) => {
            const selected = this.#normalizeTab(tab.dataset.cmsInspectorTab) === activeKey;
            tab.classList.toggle("is-active", selected);
            tab.setAttribute("aria-selected", selected ? "true" : "false");
            tab.tabIndex = selected ? 0 : -1;
        });
        panels.forEach((panel) => {
            const selected = this.#normalizeTab(panel.dataset.cmsInspectorPanel) === activeKey;
            panel.classList.toggle("is-active", selected);
            panel.setAttribute("aria-hidden", selected ? "false" : "true");
            panel.toggleAttribute("inert", !selected);
        });
    }

    static #destroyOne(root: HTMLElement): void {
        const state = this.#instances.get(root);
        if (!state) return;
        state.disposers.forEach((dispose) => dispose());
        this.#instances.delete(root);
        this.#roots.delete(root);
        root.removeAttribute("data-inspector-tabs-mounted");
    }

    static #writeMetrics(root: HTMLElement, state: InspectorTabState): void {
        const viewport = root.querySelector<HTMLElement>("[data-cms-inspector-tab-panels]");
        const wrapper = root.querySelector<HTMLElement>(".admin-cms-inspector-tabs__wrapper");
        const slides = [...root.querySelectorAll<HTMLElement>("[data-cms-inspector-panel]")];
        const slideContentOverflow = slides.reduce((max, slide) => Math.max(max, slide.scrollWidth - slide.clientWidth), 0);
        root.dataset.inspectorTabActiveIndex = String(state.activeIndex);
        root.dataset.inspectorTabViewportWidth = String(Math.round(viewport?.getBoundingClientRect().width ?? 0));
        root.dataset.inspectorTabWrapperWidth = String(Math.round(wrapper?.getBoundingClientRect().width ?? 0));
        root.dataset.inspectorTabPanelWidths = slides.map((slide) => String(Math.round(slide.getBoundingClientRect().width))).join(",");
        root.dataset.inspectorTabHorizontalOverflow = String(Math.max(0, slideContentOverflow));
        root.dataset.inspectorTabInstanceCount = String(this.instanceCount());
    }

    static #normalizeTab(value: string | undefined): InspectorTabKey {
        return TAB_KEYS.includes(value as InspectorTabKey) ? value as InspectorTabKey : "design";
    }
}
